import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { apiFetch, resolveApiUrl } from "@/lib/jwt-auth-client";
import {
  useAdsWorkspaceStore,
  type AdAssetKind,
  type BananBatchItemLineage,
  type KlingBatchItemLineage,
  type MarcusGuidedAdsFlowId,
  type SubAgentId,
} from "@/features/ads";
import {
  useAdsResearchStore,
  type ExistingUgcElementSummary,
  type UgcElementKind,
  type UgcVideoElementPlan,
  type AdPerformanceForecast,
} from "@/features/ads";
import { appEvents } from "@/shared/lib/events";
import { emitContentStudioEvent } from "@/features/content";
import { extractWorkspaceId } from "@/shared/lib/workspace/extractWorkspaceId";
import { UGC_ELEMENT_PLAN_CONFIRM_PROMPT } from "@/features/ads/components/ElementPlanReviewModal";
import {
  useBrollProposalsStore,
  useCaptionsProposalsStore,
  useOverlayProposalsStore,
  useVideoEditorStore,
  type BrollProposal,
  type CaptionsProposal,
  type Overlay as VideoEditorOverlay,
} from "@/features/video-editor";
import {
  enqueueCursorMove,
  moveCursorAndWait,
  type CursorMoveDetail,
} from "@/features/ads/services/marcusCursorQueue";
import { useAgentChatStore } from "../store/useAgentChatStore";

/** Safety-net timeout for the transient `subAgentBusy` flag on Marcus dispatch.
 *  The user can clear it sooner by opening the sub-agent's chat (handled in
 *  `selectChatAgent`); this just guarantees the notifier doesn't linger if the
 *  managed-agent run finishes without the user ever visiting it. */
const SUB_AGENT_BUSY_TTL_MS = 5 * 60 * 1000;
export const ONBOARDING_NAVIGATION_DELAY_MS = 1500;
export const ONBOARDING_POST_HIGHLIGHT_NAVIGATION_DELAY_MS = 350;
const ONBOARDING_DESTINATION_READY_TIMEOUT_MS = 10_000;

/** Mark a managed-agent sub-agent as busy on dispatch, with a 5-min auto-clear.
 *  Used for Mads / GAds / Scout where we have no native polling card. */
function markSubAgentBusy(agentId: SubAgentId): void {
  const store = useAdsWorkspaceStore.getState();
  store.setSubAgentBusy(agentId, true);
  setTimeout(() => {
    useAdsWorkspaceStore.getState().setSubAgentBusy(agentId, false);
  }, SUB_AGENT_BUSY_TTL_MS);
}

// Companion-action envelope. Shape varies per `action` discriminator —
// each branch in `executeCompanionAction` narrows the relevant fields.
type CompanionActionOutput = {
  action?: string;
  endpoint?: string;
  method?: string;
  params?: Record<string, unknown>;
  workspace_id?: string;
  command?: string;
  flow?: string;
  kind?: string;
  status?: string;
  route?: string;
  notice?: string;
  message?: string;
  brief?: string;
  plan?: unknown;
  prefill?: unknown;
  confirmations?: unknown;
  finalAction?: string;
  highlightTarget?: string;
  agentId?: string;
  subAgentId?: string;
  targetAgentName?: string;
  autoSubmit?: boolean;
  error?: string;
  // banan_dispatched / banan_batch_dispatched / kling_*
  jobId?: string;
  sessionId?: string;
  productId?: string;
  productName?: string;
  // banan_batch_dispatched
  itemCount?: number;
  items?: unknown[];
  assetKind?: string;
  generator?: string;
  templateId?: string;
  higgsfieldMode?: string;
  attachments?: unknown;
  // kling_batch_dispatched — `sessions` is the full Vid spawn list,
  // `batchItems` is index-aligned lineage.
  batchItems?: unknown[];
  sessions?: unknown[];
  // ugc_element_refs_dispatched (one Banan session per video)
  videos?: unknown[];
  // ugc_element_plans_generated
  plans?: unknown[];
  existingElements?: unknown[];
  /** planRunId is emitted alongside plans so readers can scope active runs. */
  planRunId?: string;
  // brief_overlays_applied
  projectId?: string;
};

function asActionOutput(value: unknown): CompanionActionOutput {
  if (!value || typeof value !== "object") return {};
  return value as CompanionActionOutput;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : {};
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

function isContentStudioEndpoint(endpoint: string): boolean {
  return endpoint.startsWith("/core/content-studio/");
}

const HANDOFF_SUB_AGENT_IDS: ReadonlySet<SubAgentId> = new Set([
  "banan",
  "vid",
  "gads",
  "meta_ads",
  "higgsfield",
  "higgsfield_cli",
  "moodboard",
]);

const GUIDED_ADS_FLOW_IDS: ReadonlySet<MarcusGuidedAdsFlowId> = new Set([
  "meta_campaign",
  "google_campaign",
  "video_editor",
  "image_ad_flow",
  "video_ad_flow",
  "static_adclone",
  "video_adclone",
  "template_video",
  "trending_image_ad",
  "trending_video_ad",
]);

function asSubAgentId(value: unknown): SubAgentId | null {
  const id = asString(value);
  return id && HANDOFF_SUB_AGENT_IDS.has(id as SubAgentId) ? (id as SubAgentId) : null;
}

function asGuidedAdsFlowId(value: unknown): MarcusGuidedAdsFlowId | null {
  const id = asString(value);
  return id && GUIDED_ADS_FLOW_IDS.has(id as MarcusGuidedAdsFlowId)
    ? (id as MarcusGuidedAdsFlowId)
    : null;
}

function parsePlanSteps(value: unknown): Array<{ label: string; target?: string }> {
  const rawSteps = Array.isArray(value)
    ? value
    : Array.isArray(asRecord(value).steps)
      ? (asRecord(value).steps as unknown[])
      : [];
  return rawSteps
    .map((step) => {
      const rec = asRecord(step);
      const label = asString(rec.label);
      if (!label) return null;
      const target = asString(rec.target);
      return { label, ...(target ? { target } : {}) };
    })
    .filter((step): step is { label: string; target?: string } => step !== null);
}

function parseGuidedConfirmations(
  value: unknown,
): Array<{ id: string; label: string; target?: string }> {
  if (!Array.isArray(value)) return [];
  return value
    .map((item, index) => {
      const rec = asRecord(item);
      const id = asString(rec.id) ?? `confirmation_${index + 1}`;
      const label = asString(rec.label);
      if (!label) return null;
      const target = asString(rec.target);
      return { id, label, ...(target ? { target } : {}) };
    })
    .filter((item): item is { id: string; label: string; target?: string } => item !== null);
}

function currentAdsWorkspaceId(output?: CompanionActionOutput): string | null {
  const liveWorkspaceId =
    typeof window !== "undefined"
      ? extractWorkspaceId(window.location.pathname, window.location.search)
      : null;
  return (
    liveWorkspaceId ??
    asString(output?.workspace_id) ??
    useAdsWorkspaceStore.getState().scopeWorkspaceId ??
    null
  );
}

function withWorkspaceQuery(route: string, workspaceId: string | null): string {
  if (!workspaceId || /^https?:\/\//i.test(route)) return route;
  if (typeof window === "undefined") return route;
  try {
    const url = new URL(route, window.location.origin);
    if (!url.searchParams.has("ws")) url.searchParams.set("ws", workspaceId);
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return route;
  }
}

function highlightSelector(target: string): string {
  const escaped =
    typeof CSS !== "undefined" && typeof CSS.escape === "function"
      ? CSS.escape(target)
      : target.replace(/"/g, '\\"');
  return `[data-highlight-id="${escaped}"]`;
}

function hasHighlightTarget(target: string): boolean {
  return (
    typeof document !== "undefined" && Boolean(document.querySelector(highlightSelector(target)))
  );
}

async function waitForHighlightTarget(target: string, timeoutMs = 4000): Promise<void> {
  if (typeof document === "undefined") return;
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    if (hasHighlightTarget(target)) return;
    await new Promise((resolve) => window.setTimeout(resolve, 100));
  }
}

async function moveCursorWhenReady(
  target: string,
  comment?: string,
  pulses?: number,
): Promise<void> {
  await waitForHighlightTarget(target);
  enqueueCursorMove({ target, ...(comment ? { comment } : {}), ...(pulses ? { pulses } : {}) });
}

function toBananBatchItems(items: unknown[] | undefined): BananBatchItemLineage[] | undefined {
  if (!items?.length) return undefined;
  return items.map((item) => {
    const rec = asRecord(item);
    return {
      ...(rec.sourceResearchAngle ? { sourceResearchAngle: asString(rec.sourceResearchAngle) ?? undefined } : {}),
      ...(rec.targetProblem ? { targetProblem: asString(rec.targetProblem) ?? undefined } : {}),
      ...(rec.overlayText ? { overlayText: asString(rec.overlayText) ?? undefined } : {}),
      ...(rec.ugcSceneSlot === "hook" || rec.ugcSceneSlot === "setup" || rec.ugcSceneSlot === "reveal" || rec.ugcSceneSlot === "payoff"
        ? { ugcSceneSlot: rec.ugcSceneSlot as "hook" | "setup" | "reveal" | "payoff" }
        : {}),
      ...(typeof rec.ugcSceneIndex === "number" ? { ugcSceneIndex: rec.ugcSceneIndex } : {}),
      ...(typeof rec.ugcVideoIndex === "number" ? { ugcVideoIndex: rec.ugcVideoIndex } : {}),
      ...(rec.sourceVideoScriptAngle ? { sourceVideoScriptAngle: asString(rec.sourceVideoScriptAngle) ?? undefined } : {}),
      ...(rec.assignedAvatarId ? { assignedAvatarId: asString(rec.assignedAvatarId) ?? undefined } : {}),
      ...(rec.sourceAvatarPersonaId ? { sourceAvatarPersonaId: asString(rec.sourceAvatarPersonaId) ?? undefined } : {}),
      ...(rec.avatarName ? { avatarName: asString(rec.avatarName) ?? undefined } : {}),
      ...(rec.sourceHookType ? { sourceHookType: asString(rec.sourceHookType) ?? undefined } : {}),
    };
  });
}

function toHiggsfieldBatchItems(items: unknown[] | undefined) {
  if (!items?.length) return undefined;
  return items.map((item) => {
    const rec = asRecord(item);
    return {
      ...(rec.videoTypeId ? { videoTypeId: asString(rec.videoTypeId) ?? undefined } : {}),
      ...(rec.higgsfieldMode ? { higgsfieldMode: asString(rec.higgsfieldMode) ?? undefined } : {}),
      ...(rec.variantAngle ? { variantAngle: asString(rec.variantAngle) ?? undefined } : {}),
      ...(rec.productId ? { productId: asString(rec.productId) ?? undefined } : {}),
      ...(rec.productName ? { productName: asString(rec.productName) ?? undefined } : {}),
      ...(rec.aspectRatio ? { aspectRatio: asString(rec.aspectRatio) ?? undefined } : {}),
      ...(typeof rec.duration === "number" ? { duration: rec.duration } : {}),
    };
  });
}

/**
 * Companion tool-dispatch side-effects.
 *
 * Receives the raw `output` payload from a completed Anthropic tool call
 * (via `useChat`'s `onFinish`) and routes it to the right Zustand store
 * mutations / app events. Stateless: returns a stable `useCallback` with
 * empty deps — every store read is fresh via `getState()`.
 */
export function useCompanionToolDispatch(): {
  executeCompanionAction: (outputRaw: unknown) => Promise<void>;
} {
  const navigate = useNavigate();

  const applyMarcusNavigationAndHandoff = useCallback(
    async (output: CompanionActionOutput) => {
      const store = useAdsWorkspaceStore.getState();
      const workspaceId = currentAdsWorkspaceId(output);
      const route = asString(output.route);
      const notice = asString(output.notice) ?? asString(output.message) ?? undefined;
      const highlightTarget = asString(output.highlightTarget);
      const agentId = asSubAgentId(output.agentId ?? output.subAgentId);
      const sessionId = asString(output.sessionId);
      const brief = asString(output.brief);

      if (workspaceId) store.setScopeWorkspace(workspaceId);
      if (route) navigate(withWorkspaceQuery(route, workspaceId));
      if (notice) toast(notice);
      if (highlightTarget) void moveCursorWhenReady(highlightTarget, notice);

      if (agentId) {
        store.revealSubAgent(agentId);
        if (sessionId) {
          store.setSubAgentSessionId(agentId, sessionId);
          store.cacheSubAgentMessages(agentId, []);
        }
        store.selectChatAgent(agentId);
        useAgentChatStore.getState().requestDockAutoOpen("agent-dispatch");
        if (brief) {
          store.requestSubAgentInput(agentId, brief, {
            autoSubmit: output.autoSubmit ?? true,
          });
        }
        if (agentId === "gads" || agentId === "meta_ads" || agentId === "higgsfield" || agentId === "higgsfield_cli") {
          markSubAgentBusy(agentId);
        }
      }

      if (output.targetAgentName === "marcus_video_editor" && brief) {
        useAgentChatStore.getState().requestDockAutoOpen("chat-injection");
        const publishPrompt = () => {
          appEvents.publish({
            type: "companion:request-prompt",
            text: brief,
            display: {
              kind: "command",
              label: "Video edit brief",
              source: "marcus",
            },
          });
        };
        if (highlightTarget) {
          void waitForHighlightTarget(highlightTarget).then(publishPrompt);
        } else {
          publishPrompt();
        }
      }
    },
    [navigate],
  );

  const executeCompanionAction = useCallback(async (outputRaw: unknown) => {
    const output = asActionOutput(outputRaw);

    if (output.action === "ads_execution_failed") {
      const message = asString(output.error) ?? "Marcus could not complete that ads action.";
      toast.error(message);
      return;
    }

    if (output.action === "ads_ui") {
      if (output.command === "highlight") {
        const params = output.params ?? {};
        const target = asString(params.target);
        if (target) {
          void moveCursorWhenReady(
            target,
            asString(params.comment) ?? undefined,
            typeof params.pulses === "number" ? params.pulses : undefined,
          );
        }
        return;
      }
      if (output.command) {
        appEvents.publish({
          type: "ads-ui-action",
          command: output.command,
          params: output.params ?? {},
        });
      }
      return;
    }

    if (output.action === "ads_plan_announced") {
      const steps = Array.isArray((output as Record<string, unknown>).steps)
        ? ((output as Record<string, unknown>).steps as unknown[])
            .map((step) => {
              const rec = asRecord(step);
              const label = asString(rec.label);
              if (!label) return null;
              const target = asString(rec.target);
              return { label, ...(target ? { target } : {}) };
            })
            .filter((step): step is { label: string; target?: string } => step !== null)
        : [];
      if (steps.length > 0) {
        appEvents.publish({ type: "marcus-plan-announced", steps });
      }
      return;
    }

    if (output.action === "ads_plan_advance") {
      appEvents.publish({
        type: "marcus-plan-advance",
        ...(output.highlightTarget ? { target: output.highlightTarget } : {}),
        ...(typeof (output as Record<string, unknown>).target === "string"
          ? { target: (output as Record<string, unknown>).target as string }
          : {}),
        ...(typeof (output as Record<string, unknown>).index === "number"
          ? { index: (output as Record<string, unknown>).index as number }
          : {}),
        ...(typeof (output as Record<string, unknown>).labelHint === "string"
          ? { labelHint: (output as Record<string, unknown>).labelHint as string }
          : {}),
      });
      return;
    }

    if (output.action === "marcus_onboarding_step") {
      const raw = output as Record<string, unknown>;
      const plan = asRecord(raw.plan);
      const planSteps = Array.isArray(plan.steps)
        ? (plan.steps as unknown[])
            .map((step) => {
              const rec = asRecord(step);
              const label = asString(rec.label);
              if (!label) return null;
              const target = asString(rec.target);
              return { label, ...(target ? { target } : {}) };
            })
            .filter((step): step is { label: string; target?: string } => step !== null)
        : [];
      if (planSteps.length > 0) {
        appEvents.publish({ type: "marcus-plan-announced", steps: planSteps });
      }
      const planAdvanceTarget = asString(raw.planAdvanceTarget);
      if (planAdvanceTarget) {
        appEvents.publish({ type: "marcus-plan-advance", target: planAdvanceTarget });
      }
      const navigateInfo = asRecord(raw.navigate);
      const navigateRoute = asString(navigateInfo.route);
      const postHighlightNavigateInfo = asRecord(raw.postHighlightNavigate);
      const postHighlightNavigateTarget = asString(postHighlightNavigateInfo.target);
      const postHighlightNavigateRoute = asString(postHighlightNavigateInfo.route);
      const showAgentList = raw.showAgentList === true || asString(raw.stage) === "agents_list";
      const allHighlights: CursorMoveDetail[] = Array.isArray(raw.highlights)
        ? (raw.highlights as unknown[])
            .map((h): CursorMoveDetail | null => {
              const rec = asRecord(h);
              const target = asString(rec.target);
              if (!target) return null;
              return {
                target,
                ...(asString(rec.comment) ? { comment: asString(rec.comment) as string } : {}),
                ...(typeof rec.pulses === "number" ? { pulses: rec.pulses } : {}),
                source: "onboarding" as const,
              };
            })
            .filter((h): h is CursorMoveDetail => h !== null)
        : [];
      const postHighlightNavigateIndex = postHighlightNavigateTarget
        ? allHighlights.findIndex((h) => h.target === postHighlightNavigateTarget)
        : -1;
      const highlights =
        postHighlightNavigateIndex >= 0
          ? allHighlights.slice(0, postHighlightNavigateIndex + 1)
          : allHighlights;
      const postNavigationHighlights =
        postHighlightNavigateIndex >= 0
          ? allHighlights.slice(postHighlightNavigateIndex + 1)
          : [];
      const workspaceId = currentAdsWorkspaceId(output);
      if (workspaceId) {
        useAdsWorkspaceStore.getState().setScopeWorkspace(workspaceId);
      }

      const enqueueRemaining = (rest: typeof highlights) => {
        for (const h of rest) enqueueCursorMove(h);
      };

      if (showAgentList) {
        const chat = useAgentChatStore.getState();
        chat.requestDockAutoOpen("onboarding-showcase");
        useAdsWorkspaceStore.getState().goBackToChatList();

        const publishShowcaseAndQueue = () => {
          if (highlights.length === 0) {
            appEvents.publish({ type: "marcus-onboarding:agent-list-showcase", active: true });
            return;
          }
          if (typeof window === "undefined") {
            appEvents.publish({ type: "marcus-onboarding:agent-list-showcase", active: true });
            enqueueRemaining(highlights);
            return;
          }
          let attempts = 0;
          const revealUntilMounted = () => {
            appEvents.publish({ type: "marcus-onboarding:agent-list-showcase", active: true });
            if (
              hasHighlightTarget(highlights[0].target) ||
              attempts * 100 >= ONBOARDING_DESTINATION_READY_TIMEOUT_MS
            ) {
              void waitForHighlightTarget(
                highlights[0].target,
                ONBOARDING_DESTINATION_READY_TIMEOUT_MS,
              ).then(() => enqueueRemaining(highlights));
              return;
            }
            attempts += 1;
            window.setTimeout(revealUntilMounted, 100);
          };
          revealUntilMounted();
        };

        if (typeof window === "undefined") {
          publishShowcaseAndQueue();
        } else {
          window.setTimeout(publishShowcaseAndQueue, 60);
        }
        return;
      }

      if (
        postHighlightNavigateTarget &&
        postHighlightNavigateRoute &&
        typeof window !== "undefined"
      ) {
        let cleanupTimer: number | null = null;
        let unsubscribe: (() => void) | null = null;
        const cleanup = () => {
          if (cleanupTimer !== null) {
            window.clearTimeout(cleanupTimer);
            cleanupTimer = null;
          }
          unsubscribe?.();
          unsubscribe = null;
        };
        unsubscribe = appEvents.subscribe("marcus-cursor-arrived", (event) => {
          if (event.source && event.source !== "onboarding") return;
          if (event.target !== postHighlightNavigateTarget) return;
          cleanup();
          window.setTimeout(() => {
            navigate(withWorkspaceQuery(postHighlightNavigateRoute, workspaceId));
            if (postNavigationHighlights.length > 0) {
              void waitForHighlightTarget(
                postNavigationHighlights[0].target,
                ONBOARDING_DESTINATION_READY_TIMEOUT_MS,
              ).then(() => enqueueRemaining(postNavigationHighlights));
            }
          }, ONBOARDING_POST_HIGHLIGHT_NAVIGATION_DELAY_MS);
        });
        cleanupTimer = window.setTimeout(cleanup, ONBOARDING_DESTINATION_READY_TIMEOUT_MS);
      }

      if (navigateRoute && highlights.length > 0) {
        // Highlight the sidebar item on the current page, then navigate part
        // way through the first stop. Destination highlights are only queued
        // after their DOM target mounts, keeping speech and spotlight aligned
        // even when the route chunk loads slowly.
        const [firstHighlight, ...rest] = highlights;
        const highlightBeforeNavigate =
          firstHighlight.target.startsWith("sidebar-nav-") ||
          hasHighlightTarget(firstHighlight.target);
        if (highlightBeforeNavigate) {
          void moveCursorAndWait(firstHighlight, ONBOARDING_NAVIGATION_DELAY_MS).then(() => {
            navigate(withWorkspaceQuery(navigateRoute, workspaceId));
            if (rest.length > 0) {
              void waitForHighlightTarget(
                rest[0].target,
                ONBOARDING_DESTINATION_READY_TIMEOUT_MS,
              ).then(() => enqueueRemaining(rest));
            }
          });
        } else {
          navigate(withWorkspaceQuery(navigateRoute, workspaceId));
          void waitForHighlightTarget(
            firstHighlight.target,
            ONBOARDING_DESTINATION_READY_TIMEOUT_MS,
          ).then(() => enqueueRemaining(highlights));
        }
      } else {
        if (navigateRoute) {
          navigate(withWorkspaceQuery(navigateRoute, workspaceId));
        }
        enqueueRemaining(highlights);
      }
      return;
    }

    if (
      output.action === "ads_orchestrator_handoff" ||
      output.action === "ads_orchestrator_open_flow"
    ) {
      await applyMarcusNavigationAndHandoff(output);
      return;
    }

    if (output.action === "ads_guided_flow") {
      const flow = asGuidedAdsFlowId(output.flow);
      const route = asString(output.route);
      if (!flow || !route) return;
      const store = useAdsWorkspaceStore.getState();
      const workspaceId = currentAdsWorkspaceId(output);
      const notice = asString(output.notice) ?? undefined;
      const plan = parsePlanSteps(output.plan);

      if (workspaceId) store.setScopeWorkspace(workspaceId);
      store.setPendingMarcusGuidedFlow({
        id: `marcus-guided-${flow}-${Date.now()}`,
        flow,
        route,
        plan,
        prefill: asRecord(output.prefill),
        confirmations: parseGuidedConfirmations(output.confirmations),
        ...(asString(output.finalAction) ? { finalAction: asString(output.finalAction) as string } : {}),
        ...(notice ? { notice } : {}),
        workspaceId,
        createdAt: Date.now(),
      });
      if (plan.length > 0) appEvents.publish({ type: "marcus-plan-announced", steps: plan });
      store.selectChatAgent(null);
      useAgentChatStore.getState().requestDockAutoOpen("agent-dispatch");
      if (notice) toast(notice);
      navigate(withWorkspaceQuery(route, workspaceId));
      return;
    }

    if (output.action === "ads_execution_result") {
      await applyMarcusNavigationAndHandoff(output);
      const store = useAdsWorkspaceStore.getState();
      const agentId = asSubAgentId(output.agentId);
      const productId = asString(output.productId);
      const productName = asString(output.productName);
      const sessionId = asString(output.sessionId);
      const itemCount = typeof output.itemCount === "number" ? output.itemCount : undefined;

      if (agentId === "banan" && output.jobId) {
        const rawAssetKind = asString(output.assetKind);
        const assetKind: AdAssetKind | undefined =
          rawAssetKind === "image-ad" || rawAssetKind === "ugc-portrait" || rawAssetKind === "ugc-reference"
            ? rawAssetKind
            : "image-ad";
        store.addPendingBananJob({
          jobId: output.jobId,
          status: "submitted",
          dispatchedAt: Date.now(),
          ...(assetKind ? { assetKind } : {}),
          ...(productId ? { productId } : {}),
          ...(productName ? { productName } : {}),
          ...(toBananBatchItems(output.items) ? { batchItems: toBananBatchItems(output.items) } : {}),
          ...(itemCount ? { itemCount } : {}),
          ...(sessionId ? { sessionId } : {}),
        });
      }

      if ((agentId === "higgsfield" || agentId === "higgsfield_cli") && output.jobId) {
        store.addPendingHiggsfieldJob({
          jobId: output.jobId,
          status: "submitted",
          dispatchedAt: Date.now(),
          ...(productId ? { productId } : {}),
          ...(productName ? { productName } : {}),
          ...(output.templateId ? { videoTypeId: output.templateId } : {}),
          ...(output.higgsfieldMode ? { higgsfieldMode: output.higgsfieldMode } : {}),
          generator: output.generator === "higgsfield_cli" ? "higgsfield_cli" : "higgsfield",
          ...(toHiggsfieldBatchItems(output.batchItems) ? { batchItems: toHiggsfieldBatchItems(output.batchItems) } : {}),
          ...(itemCount ? { itemCount } : {}),
          ...(sessionId ? { sessionId } : {}),
        });
      }

      return;
    }

    // Handle Banan image generation dispatched — register in store for tracking
    // and link the headless session so SubAgentChatView can resume it.
    // addPendingBananJob reveals Banan in the chat list; the user opens it
    // manually rather than being auto-navigated away from Marcus.
    if (output.action === "banan_dispatched" && output.jobId) {
      const productId = asString(output.productId);
      const productName = asString(output.productName);
      const bananSessionId = asString(output.sessionId);
      const store = useAdsWorkspaceStore.getState();
      store.addPendingBananJob({
        jobId: output.jobId,
        status: "submitted",
        dispatchedAt: Date.now(),
        ...(productId ? { productId } : {}),
        ...(productName ? { productName } : {}),
        ...(bananSessionId ? { sessionId: bananSessionId } : {}),
      });
      if (bananSessionId) {
        store.setSubAgentSessionId("banan", bananSessionId);
        store.cacheSubAgentMessages("banan", []);
      }
      return;
    }

    // Handle Banan batch dispatch — single session, single job with batch metadata.
    // Link the session so the user can chat with Agent Banan about all images.
    if (output.action === "banan_batch_dispatched" && output.jobId) {
      const productId = asString(output.productId);
      const productName = asString(output.productName);
      const bananSessionId = asString(output.sessionId);
      const itemCount = typeof output.itemCount === "number" ? output.itemCount : undefined;
      const rawAssetKind = asString(output.assetKind);
      const assetKind: AdAssetKind | undefined =
        rawAssetKind === "image-ad" || rawAssetKind === "ugc-portrait" || rawAssetKind === "ugc-reference"
          ? rawAssetKind
          : undefined;
      const store = useAdsWorkspaceStore.getState();

      // Extract per-item lineage metadata from the items array
      const batchItems: BananBatchItemLineage[] | undefined = Array.isArray(output.items)
        ? output.items.map((item: unknown) => {
            const rec = asRecord(item);
            return {
              ...(rec.sourceResearchAngle ? { sourceResearchAngle: asString(rec.sourceResearchAngle) ?? undefined } : {}),
              ...(rec.targetProblem ? { targetProblem: asString(rec.targetProblem) ?? undefined } : {}),
              ...(rec.overlayText ? { overlayText: asString(rec.overlayText) ?? undefined } : {}),
              ...(rec.ugcSceneSlot === "hook" || rec.ugcSceneSlot === "setup" || rec.ugcSceneSlot === "reveal" || rec.ugcSceneSlot === "payoff"
                ? { ugcSceneSlot: rec.ugcSceneSlot as "hook" | "setup" | "reveal" | "payoff" }
                : {}),
              ...(typeof rec.ugcSceneIndex === "number" ? { ugcSceneIndex: rec.ugcSceneIndex } : {}),
              ...(typeof rec.ugcVideoIndex === "number" ? { ugcVideoIndex: rec.ugcVideoIndex } : {}),
              ...(rec.sourceVideoScriptAngle ? { sourceVideoScriptAngle: asString(rec.sourceVideoScriptAngle) ?? undefined } : {}),
              ...(rec.assignedAvatarId ? { assignedAvatarId: asString(rec.assignedAvatarId) ?? undefined } : {}),
              ...(rec.sourceAvatarPersonaId ? { sourceAvatarPersonaId: asString(rec.sourceAvatarPersonaId) ?? undefined } : {}),
              ...(rec.avatarName ? { avatarName: asString(rec.avatarName) ?? undefined } : {}),
              ...(rec.sourceHookType ? { sourceHookType: asString(rec.sourceHookType) ?? undefined } : {}),
            };
          })
        : undefined;

      store.addPendingBananJob({
        jobId: output.jobId as string,
        status: "submitted",
        dispatchedAt: Date.now(),
        ...(assetKind ? { assetKind } : {}),
        ...(productId ? { productId } : {}),
        ...(productName ? { productName } : {}),
        ...(batchItems ? { batchItems } : {}),
        ...(itemCount ? { itemCount } : {}),
        ...(bananSessionId ? { sessionId: bananSessionId } : {}),
      });
      if (bananSessionId) {
        store.setSubAgentSessionId("banan", bananSessionId);
        store.cacheSubAgentMessages("banan", []);
      }
      return;
    }

    // Handle UGC element plans generation — surface the per-script plans
    // in the research store so AdsWorkspace can render them for review.
    if (output.action === "ugc_element_plans_generated" && Array.isArray(output.plans)) {
      const plans: Array<{ scriptAngle: string; plan: UgcVideoElementPlan }> = [];
      for (const raw of output.plans as unknown[]) {
        const rec = asRecord(raw);
        const scriptAngle = asString(rec.scriptAngle);
        const plan = rec.plan;
        if (scriptAngle && plan && typeof plan === "object") {
          plans.push({ scriptAngle, plan: plan as UgcVideoElementPlan });
        }
      }
      const validKinds: ReadonlySet<UgcElementKind> = new Set([
        "avatar-anchor",
        "avatar-pose",
        "product",
        "item",
        "background",
      ]);
      const existing: ExistingUgcElementSummary[] = [];
      if (Array.isArray(output.existingElements)) {
        for (const raw of output.existingElements as unknown[]) {
          const rec = asRecord(raw);
          const elementKey = asString(rec.elementKey);
          const kind = asString(rec.kind);
          if (!elementKey || !kind || !validKinds.has(kind as UgcElementKind)) continue;
          existing.push({
            elementKey,
            kind: kind as UgcElementKind,
            ...(typeof rec.klingElementId === "number" ? { klingElementId: rec.klingElementId } : {}),
          });
        }
      }
      if (plans.length > 0) {
        const store = useAdsResearchStore.getState();
        // Plans are also persisted server-side as product-scoped
        // `ugc-video-plan` artifacts; consumer surfaces read from those via
        // `useElementPlansForProduct`. This transient mirror exists so the
        // planning surface can render immediately, before the artifact list
        // refetches.
        store.setElementPlans(plans);
        store.setExistingElements(existing);
        const planRunId = asString(output.planRunId);
        if (planRunId) store.setActivePlanRunId(planRunId);
        // Skip the manual review modal and immediately chain forward to
        // ads_generate_ugc_element_refs (Banan) → kling video dispatch by
        // firing the same confirm prompt the modal would have sent. The user
        // still ends up in Video Polish with a live placeholder once Marcus
        // emits kling_batch_dispatched (handled below).
        useAdsWorkspaceStore.getState().requestCompanionPrompt(
          UGC_ELEMENT_PLAN_CONFIRM_PROMPT,
        );
      }
      return;
    }

    // Handle UGC element-refs dispatch — ONE Banan session per video. Each
    // entry in output.videos describes one video; sessionId/jobId are present
    // only when work was dispatched (fully-cached videos return without a
    // jobId). Register each dispatched job as a pending Banan job and link
    // the first session as the active Banan sub-agent.
    //
    // Lineage: the dispatch tool's task builder
    // (`imageDispatchTools.ts:688,702`) pushes items to Banan in
    // `[...newElements, ...textFrames]` order, and
    // `sessionService.ts:collectedImageUrls` appends URLs in
    // tool-invocation order. We mirror that contract here so
    // `completeBananJob` can zip `imageUrls[i] → batchItems[i]` and write
    // per-image `elementKey`/`kind`/`assignedBeat`/`ugcVideoIndex` onto each
    // resulting GeneratedAd's metadata.
    if (output.action === "ugc_element_refs_dispatched" && Array.isArray(output.videos)) {
      const productId = asString(output.productId);
      const productName = asString(output.productName);
      const store = useAdsWorkspaceStore.getState();

      let firstSessionId: string | null = null;
      const validKinds: ReadonlySet<UgcElementKind> = new Set([
        "avatar-anchor",
        "avatar-pose",
        "product",
        "item",
        "background",
      ]);

      for (const raw of output.videos) {
        const video = asRecord(raw);
        const jobId = asString(video.jobId);
        if (!jobId) continue;
        const sessionId = asString(video.sessionId);
        const scriptAngle = asString(video.scriptAngle);
        const videoPlanRunId = asString(video.planRunId);
        const ugcVideoIndex =
          typeof video.videoIndex === "number" ? video.videoIndex : undefined;

        const newElements = Array.isArray(video.newElements)
          ? (video.newElements as unknown[])
          : [];
        const textFrames = Array.isArray(video.textFrames)
          ? (video.textFrames as unknown[])
          : [];

        const batchItems: BananBatchItemLineage[] = [];
        for (const rawEl of newElements) {
          const el = asRecord(rawEl);
          const elementKey = asString(el.elementKey);
          const kindStr = asString(el.kind);
          const kind =
            kindStr && validKinds.has(kindStr as UgcElementKind)
              ? (kindStr as UgcElementKind)
              : undefined;
          batchItems.push({
            ...(elementKey ? { elementKey } : {}),
            ...(kind ? { kind } : {}),
            ...(ugcVideoIndex !== undefined ? { ugcVideoIndex } : {}),
            ...(scriptAngle ? { sourceVideoScriptAngle: scriptAngle } : {}),
          });
        }
        for (const rawTf of textFrames) {
          const tf = asRecord(rawTf);
          const assignedBeat = asString(tf.assignedBeat);
          batchItems.push({
            ...(assignedBeat ? { assignedBeat } : {}),
            ...(ugcVideoIndex !== undefined ? { ugcVideoIndex } : {}),
            ...(scriptAngle ? { sourceVideoScriptAngle: scriptAngle } : {}),
          });
        }

        store.addPendingBananJob({
          jobId,
          status: "submitted",
          dispatchedAt: Date.now(),
          assetKind: "ugc-reference",
          ...(productId ? { productId } : {}),
          ...(productName ? { productName } : {}),
          ...(scriptAngle ? { scriptAngle } : {}),
          ...(sessionId ? { sessionId } : {}),
          ...(videoPlanRunId ? { planRunId: videoPlanRunId } : {}),
          ...(batchItems.length > 0
            ? { batchItems, itemCount: batchItems.length }
            : {}),
        });

        if (!firstSessionId && sessionId) firstSessionId = sessionId;
      }

      if (firstSessionId) {
        store.setSubAgentSessionId("banan", firstSessionId);
        store.cacheSubAgentMessages("banan", []);
      }
      return;
    }

    // Handle Kling batch dispatch. Each entry in `output.sessions` is its own
    // Vid session producing one video, so register one pending job per session
    // (index-aligned with `batchItems`) — that way every KlingJobCard polls
    // independently and `handleKlingJobComplete` can find a pending entry for
    // each jobId. Single-video dispatches still fall through with one entry.
    if (output.action === "kling_batch_dispatched" && output.jobId) {
      const productId = asString(output.productId);
      const productName = asString(output.productName);
      const vidSessionId = asString(output.sessionId);
      const store = useAdsWorkspaceStore.getState();

      const pickStr = (rec: Record<string, unknown>, key: string): string | undefined =>
        typeof rec[key] === "string" && (rec[key] as string).trim()
          ? (rec[key] as string)
          : undefined;

      const toLineage = (raw: unknown): KlingBatchItemLineage => {
        const rec = asRecord(raw);
        return {
          ...(pickStr(rec, "mode") ? { mode: pickStr(rec, "mode") } : {}),
          ...(pickStr(rec, "sourceImageJobId") ? { sourceImageJobId: pickStr(rec, "sourceImageJobId") } : {}),
          ...(pickStr(rec, "sourceImageUrl") ? { sourceImageUrl: pickStr(rec, "sourceImageUrl") } : {}),
          ...(pickStr(rec, "sourceResearchAngle") ? { sourceResearchAngle: pickStr(rec, "sourceResearchAngle") } : {}),
          ...(pickStr(rec, "sourceVideoScriptAngle") ? { sourceVideoScriptAngle: pickStr(rec, "sourceVideoScriptAngle") } : {}),
          ...(pickStr(rec, "targetProblem") ? { targetProblem: pickStr(rec, "targetProblem") } : {}),
          ...(pickStr(rec, "aspectRatio") ? { aspectRatio: pickStr(rec, "aspectRatio") } : {}),
          ...(pickStr(rec, "productName") ? { productName: pickStr(rec, "productName") } : {}),
        };
      };

      const batchItemsRaw: unknown[] = Array.isArray(output.batchItems)
        ? (output.batchItems as unknown[])
        : [];
      const sessionsRaw: unknown[] = Array.isArray(output.sessions)
        ? (output.sessions as unknown[])
        : [];

      // Normalise the sessions list. Fallback to a single synthetic entry so
      // the single-dispatch path (or legacy tool output without `sessions`)
      // still registers one pending job.
      const sessions: Array<{ jobId: string; sessionId?: string }> =
        sessionsRaw.length > 0
          ? sessionsRaw
              .map((raw) => {
                const rec = asRecord(raw);
                const jobId = asString(rec.jobId);
                if (!jobId) return null;
                const sid = asString(rec.sessionId);
                return { jobId, ...(sid ? { sessionId: sid } : {}) };
              })
              .filter((s): s is { jobId: string; sessionId?: string } => s !== null)
          : [{ jobId: output.jobId as string, ...(vidSessionId ? { sessionId: vidSessionId } : {}) }];

      sessions.forEach((session, index) => {
        const lineage = toLineage(batchItemsRaw[index]);
        store.addPendingKlingJob({
          jobId: session.jobId,
          status: "submitted",
          dispatchedAt: Date.now(),
          ...(productId ? { productId } : {}),
          ...(productName ?? lineage.productName
            ? { productName: productName ?? lineage.productName! }
            : {}),
          ...(lineage.sourceImageJobId ? { sourceImageJobId: lineage.sourceImageJobId } : {}),
          ...(lineage.sourceImageUrl ? { sourceImageUrl: lineage.sourceImageUrl } : {}),
          ...(lineage.sourceResearchAngle ? { sourceResearchAngle: lineage.sourceResearchAngle } : {}),
          ...(lineage.sourceVideoScriptAngle ? { sourceVideoScriptAngle: lineage.sourceVideoScriptAngle } : {}),
          ...(lineage.targetProblem ? { targetProblem: lineage.targetProblem } : {}),
        });
      });

      // Link the first session so SubAgentChatView's "vid" tab resumes where
      // the dispatch started — matches the `ugc_element_refs_dispatched` flow.
      const firstSessionId = sessions[0]?.sessionId ?? vidSessionId;
      if (firstSessionId) {
        store.setSubAgentSessionId("vid", firstSessionId);
        store.cacheSubAgentMessages("vid", []);
      }

      // Hand the user off to Video Polish so the shimmer placeholders for the
      // freshly-registered Kling jobs are visible. AdsWorkspace subscribes to
      // `activeTab` and maps "editor" → wizard step "video".
      store.setActiveTab("editor");
      return;
    }

    // Handle Google Ads agent dispatch — reveal the gads sub-agent in the
    // chat list and link the headless session so SubAgentChatView can resume
    // it. No per-job card (yet) — the agent runs server-side with its own
    // tool panel, so we just surface the chat pill.
    if (output.action === "gads_agent_dispatched") {
      const gadsSessionId = asString(output.sessionId);
      const store = useAdsWorkspaceStore.getState();
      store.revealSubAgent("gads");
      useAgentChatStore.getState().requestDockAutoOpen("agent-dispatch");
      if (gadsSessionId) {
        store.setSubAgentSessionId("gads", gadsSessionId);
        store.cacheSubAgentMessages("gads", []);
      }
      markSubAgentBusy("gads");
      return;
    }

    // Handle Meta Ads agent dispatch — same pattern as gads. The agent runs
    // against the Pipeboard MCP headless; we just surface the chat pill and
    // bind the returned sessionId so SubAgentChatView can stream it.
    if (output.action === "meta_ads_agent_dispatched") {
      const metaAdsSessionId = asString(output.sessionId);
      const store = useAdsWorkspaceStore.getState();
      store.revealSubAgent("meta_ads");
      useAgentChatStore.getState().requestDockAutoOpen("agent-dispatch");
      if (metaAdsSessionId) {
        store.setSubAgentSessionId("meta_ads", metaAdsSessionId);
        store.cacheSubAgentMessages("meta_ads", []);
      }
      markSubAgentBusy("meta_ads");
      return;
    }

    // Handle Agent Moodboard dispatch from Marcus. Moodboard is an
    // interactive managed-agent session (same pattern as scout): bind the
    // returned session id, reveal the moodboard pill, and mark busy so the
    // user knows there's work running in the background. The moodboard
    // sub-agent pane consumes the per-tool side-effects (Banan jobs for
    // portraits / element refs) once the user opens it.
    if (output.action === "moodboard_dispatched") {
      const moodboardSessionId = asString(output.sessionId);
      const store = useAdsWorkspaceStore.getState();
      store.revealSubAgent("moodboard");
      useAgentChatStore.getState().requestDockAutoOpen("agent-dispatch");
      if (moodboardSessionId) {
        store.setSubAgentSessionId("moodboard", moodboardSessionId);
        store.cacheSubAgentMessages("moodboard", []);
      }
      markSubAgentBusy("moodboard");
      return;
    }

    // Handle Agent Scout dispatch from Marcus. Scout is an interactive
    // managed-agent session, so bind the returned session id and ask the Ads
    // workspace to open the Research tab against that session.
    if (output.action === "scout_research_dispatched") {
      const scoutSessionId = asString(output.sessionId);
      const productId = asString(output.productId);
      const productName = asString(output.productName);
      const store = useAdsWorkspaceStore.getState();
      store.revealSubAgent("scout");
      useAgentChatStore.getState().requestDockAutoOpen("agent-dispatch");
      if (scoutSessionId) {
        store.setSubAgentSessionId("scout", scoutSessionId);
        store.cacheSubAgentMessages("scout", []);
        appEvents.publish({
          type: "ads-ui-action",
          command: "open_scout_research",
          params: {
            sessionId: scoutSessionId,
            ...(productId ? { productId } : {}),
            ...(productName ? { productName } : {}),
          },
        });
      }
      markSubAgentBusy("scout");
      return;
    }

    // Handle performance forecast returned directly from the backend tool
    if (output.action === "performance_forecast_ready") {
      const forecast = (output as Record<string, unknown>).forecast as AdPerformanceForecast | undefined;
      if (forecast) {
        const productId = asString((output as Record<string, unknown>).product_id) ?? null;
        useAdsWorkspaceStore.getState().setPerformanceForecast(forecast, productId);
      }
      return;
    }

    if (output.action === "video_editor_ui") {
      const projectId = asString(output.projectId);
      const command =
        output.command === "focus" ||
        output.command === "playback" ||
        output.command === "save"
          ? output.command
          : null;
      if (projectId && command) {
        appEvents.publish({
          type: "video-editor-ui-action",
          command,
          projectId,
          params: output.params ?? {},
        });
      }
      return;
    }

    if (
      output.action === "brief_overlays_applied" ||
      output.action === "video_edit_spec_applied" ||
      (output.action === "video_editor_image_assets_ready" &&
        typeof (output as Record<string, unknown>).insertedOverlayCount === "number")
    ) {
      const projectId = asString(output.projectId);
      if (projectId) {
        appEvents.publish({
          type: "ads-ui-action",
          command: "refresh_video_project",
          params: { projectId },
        });
        appEvents.publish({
          type: "video-editor:refresh-project",
          projectId,
        });
      }
      return;
    }

    // Marcus's propose_video_overlays returned a dry-run plan — lift it
    // into the proposals store so MarcusSuggestionsPanel renders cards
    // for the user to accept/edit/discard. Nothing persists until the
    // user clicks Apply, which triggers a follow-up upsert_video_overlays
    // via a separate Marcus prompt.
    if (output.action === "video_overlay_proposals_ready") {
      const projectId = asString(output.projectId);
      const raw = output as Record<string, unknown>;
      const proposals = Array.isArray(raw.proposals)
        ? (raw.proposals as VideoEditorOverlay[])
        : [];
      if (!projectId) return;
      const wouldReplaceOverlayIds = Array.isArray(raw.wouldReplaceOverlayIds)
        ? (raw.wouldReplaceOverlayIds as unknown[]).filter(
            (id): id is string => typeof id === "string",
          )
        : [];
      const roles = Array.isArray(raw.roles)
        ? (raw.roles as unknown[]).filter(
            (role): role is string => typeof role === "string",
          )
        : null;
      const durationSec =
        typeof raw.durationSec === "number" ? raw.durationSec : null;

      // Per-card regenerate path — when the user clicked 🔄 on a single
      // card, the store flagged that card id and asked Marcus for ONE
      // replacement. The result still flows through this branch, but
      // instead of wiping the whole proposals array we splice the
      // matching role's new overlay in place of the regenerating card.
      const proposalsStore = useOverlayProposalsStore.getState();
      const regeneratingCardId = proposalsStore.regeneratingCardId;
      if (regeneratingCardId && proposals.length > 0) {
        const existingCard = proposalsStore.proposals.find(
          (overlay) => overlay.id === regeneratingCardId,
        );
        const targetRole = existingCard?.role?.toLowerCase();
        // Marcus may return one overlay or a small set — pick the best
        // match for the regenerating card's role. Fall back to the
        // first proposal when no role match is found so the user still
        // sees a result instead of a silent no-op.
        const replacement =
          (targetRole &&
            proposals.find(
              (overlay) => overlay.role?.toLowerCase() === targetRole,
            )) ||
          proposals[0];
        if (replacement) {
          // Preserve the original id so React key + the user's mental
          // model of "which card am I editing" stays stable.
          const merged = { ...replacement, id: regeneratingCardId };
          proposalsStore.replaceProposalById(regeneratingCardId, merged);
          return;
        }
        // Fell through — no usable replacement. Clear the flag so the
        // card returns to the normal proposed state instead of being
        // stuck under a spinner.
        proposalsStore.endRegeneratingCard();
        return;
      }

      proposalsStore.setProposals({
        projectId,
        sourceActionId: asString(raw.sourceActionId),
        roles,
        proposals,
        wouldReplaceOverlayIds,
        durationSec,
      });
      // Expand the overlays accordion so the user sees Marcus's draft.
      appEvents.publish({
        type: "video-editor-ui-action",
        command: "focus",
        projectId,
        params: { pane: "overlays" },
      });
      return;
    }
    if (output.action === "video_overlay_proposals_error") {
      const projectId = asString(output.projectId);
      const errorMessage =
        asString((output as Record<string, unknown>).error) ??
        "Marcus could not generate overlay suggestions.";
      if (projectId) {
        const store = useOverlayProposalsStore.getState();
        // If this was a single-card regen, clear that flag instead of
        // wiping the whole proposals array. The user still sees their
        // other cards intact and can retry just the failing one.
        if (store.regeneratingCardId) {
          store.endRegeneratingCard();
        } else {
          store.setError(projectId, errorMessage);
        }
      }
      return;
    }
    // Marcus committed an overlay upsert — flip the proposals panel into
    // its `applied` success state so the user sees confirmation + Undo,
    // instead of silently returning to the idle Generate CTA. The store
    // already snapshotted the prior overlay group on Apply (beginApplying)
    // so Undo can re-upsert that snapshot.
    if (output.action === "video_overlays_upserted") {
      const projectId = asString(output.projectId);
      const raw = output as Record<string, unknown>;
      const sourceActionId = asString(raw.sourceActionId) ?? "hook-overlays";
      const appliedOverlays = Array.isArray(raw.overlays)
        ? (raw.overlays as VideoEditorOverlay[])
        : [];
      const addedIds = Array.isArray(raw.addedOverlayIds)
        ? (raw.addedOverlayIds as unknown[]).filter((id): id is string => typeof id === "string")
        : [];
      const replacedIds = Array.isArray(raw.replacedOverlayIds)
        ? (raw.replacedOverlayIds as unknown[]).filter((id): id is string => typeof id === "string")
        : [];
      if (projectId) {
        // Filter to the overlays that belong to the active group so the
        // success card renders the right count even when manual overlays
        // sit alongside Marcus's managed ones.
        const addedSet = new Set(addedIds);
        const managedAppliedOverlays = appliedOverlays.filter(
          (overlay) =>
            addedSet.has(overlay.id) ||
            overlay.source?.actionId === sourceActionId,
        );
        useOverlayProposalsStore.getState().markApplied({
          projectId,
          sourceActionId,
          appliedOverlays: managedAppliedOverlays,
          replacedIds,
        });
        // CRITICAL — drop the local draft for overlay fields so the
        // refetched server state can show through. `useEditorSpecs` and
        // the overlay editor both resolve overlays as `draftOverlays ??
        // project.overlays`; an empty `[]` draft (e.g. left over from a
        // prior session) is truthy under `??` and silently wins,
        // leaving the inspector at 0 even though the DB has the new
        // overlays. Same hazard for `draftOverlayTranslations`. Pre-
        // existing manual style/clip drafts stay untouched.
        useVideoEditorStore.getState().setDraft(projectId, {
          draftOverlays: null,
          draftOverlayTranslations: null,
        });
        // Publish the same refresh events `video_edit_spec_applied`
        // uses so the editor re-fetches the new overlays from Django.
        appEvents.publish({
          type: "ads-ui-action",
          command: "refresh_video_project",
          params: { projectId },
        });
        appEvents.publish({
          type: "video-editor:refresh-project",
          projectId,
        });
      }
      return;
    }
    if (output.action === "video_overlays_upsert_error") {
      const projectId = asString(output.projectId);
      const errorMessage =
        asString((output as Record<string, unknown>).error) ??
        "Marcus could not apply the overlay group.";
      if (projectId) {
        useOverlayProposalsStore.getState().markApplyError(projectId, errorMessage);
      }
      return;
    }

    // ── B-roll proposals (propose_video_broll / apply_video_broll) ──
    // Same propose-then-apply contract as the overlay panel above. The
    // panel kicks off `propose_video_broll`, dispatch lifts the result
    // into useBrollProposalsStore so the cards render. When the user
    // clicks Apply, `apply_video_broll` ripple-inserts kind:'broll'
    // clips with sourceAudioMode:'continue' so narration plays under
    // the cutaway visual.
    if (output.action === "video_broll_proposals_ready") {
      const projectId = asString(output.projectId);
      const raw = output as Record<string, unknown>;
      const proposals = Array.isArray(raw.proposals)
        ? (raw.proposals as BrollProposal[])
        : [];
      if (!projectId) return;
      const durationSec =
        typeof raw.durationSec === "number" ? raw.durationSec : null;
      const productRefAttached = raw.productRefAttached === true;
      useBrollProposalsStore.getState().setProposals({
        projectId,
        sourceActionId: asString(raw.sourceActionId),
        proposals,
        durationSec,
        productRefAttached,
      });
      appEvents.publish({
        type: "video-editor-ui-action",
        command: "focus",
        projectId,
        params: { pane: "clips" },
      });
      return;
    }
    if (output.action === "video_broll_proposals_error") {
      const projectId = asString(output.projectId);
      const errorMessage =
        asString((output as Record<string, unknown>).error) ??
        "Marcus could not generate b-roll suggestions.";
      if (projectId) {
        useBrollProposalsStore.getState().setError(projectId, errorMessage);
      }
      return;
    }
    if (output.action === "video_broll_upserted") {
      const projectId = asString(output.projectId);
      const raw = output as Record<string, unknown>;
      const sourceActionId = asString(raw.sourceActionId) ?? "broll-suggestions";
      const addedClipsRaw = Array.isArray(raw.addedClips)
        ? (raw.addedClips as Array<Record<string, unknown>>)
        : [];
      // Map server clip records → compact snapshot for Undo + View on timeline.
      // Derive each clip's timeline atSec from the order they were applied
      // (the apply tool inserted them ascending by atSec). We don't fight
      // for exact post-ripple coordinates here — the panel only needs an
      // id to focus + a count to display.
      const appliedClips = addedClipsRaw.map((c) => ({
        id: typeof c.id === "string" ? c.id : "",
        url: typeof c.url === "string" ? c.url : "",
        atSec: typeof c.atSec === "number" ? c.atSec : 0,
        durationSec:
          typeof c.trimEndSec === "number" && typeof c.trimStartSec === "number"
            ? c.trimEndSec - c.trimStartSec
            : 0,
      }));
      const replacedIds = Array.isArray(raw.replacedClipIds)
        ? (raw.replacedClipIds as unknown[]).filter(
            (id): id is string => typeof id === "string",
          )
        : [];
      if (projectId) {
        useBrollProposalsStore.getState().markApplied({
          projectId,
          sourceActionId,
          appliedClips,
          replacedIds,
        });
        // Refetch so the editor's clip timeline picks up the new b-roll.
        appEvents.publish({
          type: "ads-ui-action",
          command: "refresh_video_project",
          params: { projectId },
        });
        appEvents.publish({
          type: "video-editor:refresh-project",
          projectId,
        });
      }
      return;
    }
    if (output.action === "video_broll_upsert_error") {
      const projectId = asString(output.projectId);
      const errorMessage =
        asString((output as Record<string, unknown>).error) ??
        "Marcus could not apply the b-roll cutaways.";
      if (projectId) {
        useBrollProposalsStore.getState().markApplyError(projectId, errorMessage);
      }
      return;
    }

    // ── Captions proposals (propose_video_captions) ─────────────────────
    // Marcus's caption-styling tool result. Lift into the captions
    // proposals store so MarcusCaptionsSuggestionsPanel renders the
    // proposal. The panel's own HTTP path bypasses this dispatch — this
    // branch only fires when the user invokes Marcus via chat (e.g.
    // "auto-style my captions") and Marcus calls the tool directly.
    if (output.action === "video_captions_proposed") {
      const projectId = asString(output.projectId);
      const raw = output as Record<string, unknown>;
      if (!projectId) return;
      const sourceActionId = asString(raw.sourceActionId);
      const transcriptStatusRaw = asString(raw.transcriptStatus);
      const transcriptStatus =
        transcriptStatusRaw === "reused" ||
        transcriptStatusRaw === "generated" ||
        transcriptStatusRaw === "refreshed"
          ? transcriptStatusRaw
          : "generated";
      const note = asString(raw.note);
      const proposal = (raw.proposal ?? null) as CaptionsProposal | null;
      if (!proposal) {
        useCaptionsProposalsStore.getState().setEmptyProposal({
          projectId,
          note:
            note ||
            "Marcus couldn't draft a caption style for this video.",
        });
        return;
      }
      useCaptionsProposalsStore.getState().setProposal({
        projectId,
        sourceActionId,
        proposal,
        transcriptStatus,
      });
      appEvents.publish({
        type: "video-editor-ui-action",
        command: "focus",
        projectId,
        params: { pane: "styles" },
      });
      return;
    }
    if (output.action === "video_captions_proposed_error") {
      const projectId = asString(output.projectId);
      const errorMessage =
        asString((output as Record<string, unknown>).error) ??
        "Marcus could not draft a caption style.";
      if (projectId) {
        useCaptionsProposalsStore.getState().setError(projectId, errorMessage);
      }
      return;
    }

    if (output.action !== "trigger_fetch") return;

    const endpoint = output.endpoint;
    if (!endpoint) return;

    // ── Content Studio trigger_fetch — execute and emit refresh events ──
    if (isContentStudioEndpoint(endpoint)) {
      const fetchMethod = (output.method ?? "POST").toUpperCase();
      const fetchParams = output.params ?? {};
      const hasFetchBody = fetchMethod !== "GET" && fetchMethod !== "HEAD";
      const fetchUrl = new URL(resolveApiUrl(endpoint));
      if (!hasFetchBody) {
        for (const [k, v] of Object.entries(fetchParams)) {
          if (v === undefined || v === null) continue;
          fetchUrl.searchParams.set(k, String(v));
        }
      }
      try {
        await apiFetch(fetchUrl.pathname + fetchUrl.search, {
          method: fetchMethod,
          ...(hasFetchBody
            ? { headers: { "Content-Type": "application/json" }, body: JSON.stringify(fetchParams) }
            : {}),
        });
        if (endpoint.includes("/source/")) {
          emitContentStudioEvent("sources_changed");
        }
        if (endpoint.includes("/article/") || endpoint.includes("/run") || endpoint.includes("/bulk")) {
          emitContentStudioEvent("articles_changed");
        }
      } catch (err) {
        console.error("[companion] content-studio trigger_fetch failed:", err);
      }
      return;
    }

    if (endpoint !== "/api/ad-platforms/forecast-performance/") return;

    const method = (output.method ?? "POST").toUpperCase();
    const params = output.params ?? {};
    const hasBody = method !== "GET" && method !== "HEAD";
    const url = new URL(resolveApiUrl(endpoint));
    if (!hasBody) {
      for (const [k, v] of Object.entries(params)) {
        if (v === undefined || v === null) continue;
        url.searchParams.set(k, String(v));
      }
    }

    const res = await apiFetch(url.pathname + url.search, {
      method,
      ...(hasBody
        ? {
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(params),
          }
        : {}),
    });

    if (!res.ok) return;

    const data = await res.json().catch(() => null);
    if (!data) return;

    const adsStoreNext = useAdsWorkspaceStore.getState();
    const liveWorkspaceId =
      typeof window !== "undefined"
        ? extractWorkspaceId(window.location.pathname, window.location.search)
        : null;
    const workspaceForAdsContext =
      liveWorkspaceId ??
      asString(output.workspace_id) ??
      adsStoreNext.scopeWorkspaceId ??
      null;
    adsStoreNext.setScopeWorkspace(workspaceForAdsContext);

    if (endpoint === "/api/ad-platforms/forecast-performance/" && method === "POST") {
      const result = data as AdPerformanceForecast;
      const productId = asString(params.product_id) ?? null;
      adsStoreNext.setPerformanceForecast(result, productId);
      return;
    }
  }, [applyMarcusNavigationAndHandoff]);

  return { executeCompanionAction };
}
